import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2, Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { authApi, AuthApiError } from '@/lib/auth-api'

const schema = z
  .object({
    password: z
      .string()
      .min(8, 'At least 8 characters')
      .regex(/[A-Z]/, 'At least one uppercase letter')
      .regex(/[a-z]/, 'At least one lowercase letter')
      .regex(/[0-9]/, 'At least one number')
      .regex(/[^A-Za-z0-9]/, 'At least one special character'),
    confirm: z.string(),
  })
  .refine(d => d.password === d.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  })

type FormValues = z.infer<typeof schema>

function getPasswordStrength(password: string): { score: number; label: string; colour: string } {
  let score = 0
  if (password.length >= 8) score++
  if (/[A-Z]/.test(password)) score++
  if (/[a-z]/.test(password)) score++
  if (/[0-9]/.test(password)) score++
  if (/[^A-Za-z0-9]/.test(password)) score++

  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong']
  const colours = ['', 'text-red-500', 'text-orange-500', 'text-yellow-500', 'text-green-500', 'text-green-600']
  return { score, label: labels[score] ?? '', colour: colours[score] ?? '' }
}

export default function PasswordSetupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [isLoading, setIsLoading] = useState(false)
  const [tokenExpired, setTokenExpired] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [passwordValue, setPasswordValue] = useState('')

  const { score, label, colour } = getPasswordStrength(passwordValue)
  const progressValue = (score / 5) * 100

  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  // Watch password field to update strength meter
  const watchedPassword = watch('password', '')
  useEffect(() => {
    setPasswordValue(watchedPassword)
  }, [watchedPassword])

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenExpired(true)
    }
    // A full token validation would be an API call, but the server validates
    // on submit — we only need to check the token exists client-side
  }, [token])

  async function onSubmit(values: FormValues) {
    if (!token) return
    setIsLoading(true)
    try {
      await authApi.setupPassword(token, values.password)
      toast.success('Password set successfully')
      navigate('/login')
    } catch (err) {
      if (err instanceof AuthApiError && (err.status === 400 || err.status === 404)) {
        setTokenExpired(true)
      } else {
        const msg = err instanceof AuthApiError ? err.message : 'Failed to set password'
        toast.error(msg)
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (tokenExpired) {
    return (
      <Card>
        <CardHeader className="text-center">
          <CardTitle>Link expired</CardTitle>
          <CardDescription>
            This password setup link has expired or is invalid.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Please contact your account manager to request a new setup link.
          </p>
          <Button variant="outline" onClick={() => navigate('/login')}>
            Back to login
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle>Set your password</CardTitle>
        <CardDescription>
          Choose a strong password for your 360Dash account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-password">New password</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="••••••••"
                disabled={isLoading}
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-destructive">{errors.password.message}</p>
            )}

            {/* Strength indicator */}
            {passwordValue.length > 0 && (
              <div className="space-y-1">
                <Progress value={progressValue} className="h-1.5" />
                <p className={`text-xs ${colour}`}>{label}</p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Confirm password</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="••••••••"
                disabled={isLoading}
                {...register('confirm')}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showConfirm ? 'Hide confirm' : 'Show confirm'}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirm && (
              <p className="text-xs text-destructive">{errors.confirm.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading || score < 3}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Set password
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
